import { fromJS, List, Record } from 'immutable';
import {
  generateKeys,
  generateSchema,
  setInModel,
} from '../../utils/helpers/model.helper';
import {
  CompanyInfo,
  ContactDetailsEnum,
} from './sub-models/company-info.model';
import { CompanySettings } from './sub-models/company-settings/company-settings.model';
import { EmailDefault } from './sub-models/company-settings/email-default.model';
import { EmailDefaults } from './sub-models/company-settings/email-defaults.model';
import { PaymentsSettings } from './sub-models/company-settings/payments.model';
import { Tax } from './sub-models/company-settings/tax.model';
import { WithholdingTax } from './sub-models/company-settings/withholding-tax.model';
import { CustomField } from './sub-models/custom-field.model';
import { AdditionalImage } from './sub-models/document-preset-settings/additional-image.model';
import { DocNumber } from './sub-models/document-preset-settings/doc-number.model';
import { DocumentNotes } from './sub-models/document-preset-settings/document-notes.model';
import { DocumentPresetSettings } from './sub-models/document-preset-settings/document-preset-settings.model';
import { Labels } from './sub-models/document-preset-settings/labels.model';
import { Logo } from './sub-models/document-preset-settings/logo.model';
import { Rendering } from './sub-models/document-preset-settings/rendering.model';
import { Template } from './sub-models/document-preset-settings/template.model';
import { SettingsContent } from './sub-models/settings-content.model';
import { SettingsHeader } from './sub-models/settings-header.model';
import { PostPurchaseInfo } from './sub-models/post-purchase-info.model';
import { SettingsLinks } from './sub-models/settings-links.model';

export const SETTINGS_SCHEMA = Object.freeze({
  ID: '',
  LOCAL_ID: '',
  REVISION_ID: '',
  HEADER: SettingsHeader,
  CONTENT: SettingsContent,
  LINKS: SettingsLinks,
});
const RECORD = generateSchema(SETTINGS_SCHEMA);
const KEYS: any = generateKeys(SETTINGS_SCHEMA);

export class Settings extends Record(RECORD) {
  static KEYS: any = KEYS;

  constructor(props?: any) {
    super(props && props.toMap ? props.toMap() : fromJS(props || {}));

    let selfModel;

    selfModel = setInModel(this, [
      {
        key: KEYS.HEADER,
        value: new SettingsHeader(this.header),
      },
      {
        key: KEYS.CONTENT,
        value: new SettingsContent(this.content),
      },
      {
        key: KEYS.LINKS,
        value: new SettingsLinks(this.links),
      },
    ]);

    return selfModel as Settings;
  }

  get header(): SettingsHeader {
    return this.get(KEYS.HEADER);
  }

  get content(): SettingsContent {
    return this.get(KEYS.CONTENT);
  }

  get links(): SettingsLinks {
    return this.get(KEYS.LINKS);
  }

  /****
   * COMPANY INFO
   */
  get companyInfo(): CompanyInfo {
    return this.content.companyInfo;
  }

  get companyContactDetails(): List<CustomField> {
    return this.companyInfo.contactDetails;
  }

  private _companyContactDetail(contactDetailKey: ContactDetailsEnum): string {
    const detail = this.companyContactDetails.find(
      (field: CustomField) => field.key === contactDetailKey
    );

    return detail ? detail.value : '';
  }

  get companyPhone(): string {
    return this._companyContactDetail(ContactDetailsEnum.phone);
  }

  get companyEmail(): string {
    return this._companyContactDetail(ContactDetailsEnum.email);
  }

  get companyWebsite(): string {
    return this._companyContactDetail(ContactDetailsEnum.website);
  }

  get companyDescription(): string {
    return this.companyInfo.description;
  }

  get companyProfileUrl(): string {
    return this.links.profileUrl;
  }

  /****
   * COMPANY SETTINGS
   */
  get companySettings(): CompanySettings {
    return this.content.companySettings;
  }

  get taxes(): List<Tax> {
    return this.companySettings.taxes;
  }

  get withholdingTax(): WithholdingTax {
    return this.companySettings.withholdingTax;
  }

  get emailDefaults(): EmailDefaults {
    return this.companySettings.emailDefaults;
  }

  get statementEmailDefault(): EmailDefault {
    return this.emailDefaults.statement;
  }

  get taxYearStartMonth(): number {
    return this.companySettings.taxYearStartMonth;
  }

  get taxYearStartDay(): number {
    return this.companySettings.taxYearStartDay;
  }

  get payments(): PaymentsSettings {
    return this.companySettings.payments;
  }

  get isProfileEnabled(): boolean {
    return this.companySettings.profileEnabled;
  }

  /****
   * POST PURCHASE INFO
   */
  get postPurchaseInfo(): PostPurchaseInfo {
    return this.content.postPurchaseInfo;
  }

  /****
   * DOCUMENT PRESET SETTINGS
   */
  get documentPresetSettings(): DocumentPresetSettings {
    return this.content.documentPresetSettings;
  }

  get rendering(): Rendering {
    return this.documentPresetSettings.rendering;
  }

  get additionalImage(): AdditionalImage {
    return this.rendering.additionalImage;
  }

  get documentNumber(): DocNumber {
    return this.documentPresetSettings.docNumber;
  }

  get documentNotes(): DocumentNotes {
    return this.documentPresetSettings.notes;
  }

  get documentCustomFields(): List<CustomField> {
    return this.documentPresetSettings.customFields;
  }

  get documentTemplate(): Template {
    return this.documentPresetSettings.rendering.template;
  }

  get documentLogo(): Logo {
    return this.documentPresetSettings.rendering.logo;
  }

  get paymentRemindersDisabled(): boolean {
    return this.documentPresetSettings.remindersDisabled;
  }

  get separatePartsAndLabor(): boolean {
    return this.documentPresetSettings.separatePartsAndLabor;
  }

  get showDueDate(): boolean {
    return this.documentPresetSettings.showDueDate;
  }

  get showSignature(): boolean {
    return this.documentPresetSettings.showSignature;
  }

  get showPaymentTerms(): boolean {
    return this.documentPresetSettings.showPaymentTerms;
  }

  get showTaxBreakdown(): boolean {
    return this.documentPresetSettings.showTaxBreakdown;
  }

  get showQuantityAndRate(): boolean {
    return this.documentPresetSettings.showQuantityAndRate;
  }

  get showShipping(): boolean {
    return this.documentPresetSettings.showShipping;
  }

  get hideCompanyName(): boolean {
    return this.documentPresetSettings.hideCompanyName;
  }

  get terms(): number {
    return this.documentPresetSettings.terms;
  }

  get documentCurrencyCode(): string {
    return this.documentPresetSettings.currencyCode;
  }

  get documentLocale(): string {
    return this.documentPresetSettings.locale;
  }

  get documentLabels(): Labels {
    return this.documentPresetSettings.documentLabels;
  }
}
